import { z } from 'zod';
import { SourceSystem } from '../enums.js';
import { DecimalString, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';

// S-FIX-4: BE list handler omits internal fields; made optional for prod parity.
export const LandedCostVoucher = z.object({
  id: UuidString,
  entity_id: UuidString.optional(),
  voucher_no: z.string().min(1).optional(),
  posting_datetime: IsoDateTime.optional(),
  allocation_method: z.enum(['by_amount', 'by_qty', 'by_weight', 'manual']).optional(),
  is_submitted: z.boolean().default(false),
  is_cancelled: z.boolean().default(false),
  remarks: z.string().nullable().optional(),
  source_system: SourceSystem.default('staiter'),
  created_at: IsoDateTime.optional(),
  updated_at: IsoDateTime.optional(),
});
export type LandedCostVoucher = z.infer<typeof LandedCostVoucher>;

/** Landed Cost Charge — одна компонента overhead. */
export const LandedCostCharge = z.object({
  id: UuidString,
  entity_id: UuidString,
  voucher_id: UuidString,
  description: z.string().min(1),
  amount: MoneyAmount,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type LandedCostCharge = z.infer<typeof LandedCostCharge>;

/** Landed Cost Target — куда allocatable (линия исходного stock entry). */
export const LandedCostTarget = z.object({
  id: UuidString,
  entity_id: UuidString,
  voucher_id: UuidString,
  source_stock_entry_id: UuidString,
  source_stock_entry_item_id: UuidString,
  item_id: UuidString,
  warehouse_id: UuidString,
  batch_id: UuidString.nullable(),
  qty: DecimalString,
  base_amount: MoneyAmount,
  allocated_amount: MoneyAmount,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type LandedCostTarget = z.infer<typeof LandedCostTarget>;

/**
 * S-FIX-2-BE.6.4 / FE.11.3 — canonical row для GET /landed-cost list.
 * Расширяет `LandedCostVoucher` enrichment-полями, computed на handler-side:
 *  - `total_overhead` — COALESCE(SUM(charges.amount), 0); никогда не NULL.
 *  - `charges_count` / `targets_count` — int aggregates для UI badges.
 *  - `state` — computed: draft | pending_allocation | allocated.
 *
 * FE [_auth.landed-cost.tsx] table accessors используют эти поля.
 */
// S-FIX-4: prod data has list rows without these enrichment fields (handler
// returns vouchers directly when there are no charges yet). Made optional with
// defaults — UI shows '0' / 'draft' for those rows.
export const LandedCostListItem = LandedCostVoucher.extend({
  total_overhead: DecimalString.default('0'),
  charges_count: z.number().int().nonnegative().default(0),
  targets_count: z.number().int().nonnegative().default(0),
  state: z.enum(['draft', 'pending_allocation', 'allocated']).default('draft'),
});
export type LandedCostListItem = z.infer<typeof LandedCostListItem>;

export const LandedCostListResponse = z.object({
  items: z.array(LandedCostListItem),
  next_cursor: z.string().nullable().optional(),
});
export type LandedCostListResponse = z.infer<typeof LandedCostListResponse>;
